/**
 * ARAÇ ARAMA VE SEÇİM — merkezin `/resolve` uç noktasıyla.
 *
 * ÜÇ KURAL BU BİLEŞENİN BİÇİMİNİ BELİRLEDİ:
 *
 * 1. BELİRSİZLİK NORMALDİR. `/resolve` birden fazla aday ve 0..1 arası bir
 *    skor döndürür. EN YÜKSEK SKORU SESSİZCE SEÇMEK YASAK: yanlış araca
 *    bağlanmış bir hareket, sonradan fark edilmesi en zor hatadır. Bu yüzden
 *    hiçbir aday önceden işaretli gelmez, `aria-activedescendant` boş başlar
 *    ve okla gezinmeden basılan Enter hiçbir şey seçmez.
 *
 * 2. UÇAK MODU. Merkez yoksa arama devre dışı kalır ve SEBEBİ yazılır. Sessizce
 *    boş liste dönmek, "böyle bir araç yok" demekle aynı şeydir ve YANLIŞTIR.
 *
 * 3. YARIŞ. Her tuşta istek atılmaz (gecikmeli arama) ve geç dönen ESKİ yanıt
 *    yeniyi ezmez (sıra numarası). İkincisi klasik bir hata: "A" için gelen
 *    yanıt "AB" için gelenden sonra düşerse kullanıcı yanlış listeden seçer.
 */

import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { ResolveCandidateDto } from "@parafon/contracts/central-api";
import { M } from "../dil";
import { useCentral } from "../central/CentralContext";
import { assetClassLabel, currencyLabel, formatIsoTimestamp } from "../lib/format";
import { errorText } from "../lib/rpcError";
import { Button, TextInput, cx } from "../ui/primitives";

/** Bu kadar karakterin altında merkeze sorulmaz — sonuç anlamsız olur. */
export const MIN_QUERY_LENGTH = 2;

/** Gecikme: her tuşta istek atmamak için. Testler kısaltabilir. */
export const DEFAULT_DEBOUNCE_MS = 300;

type SearchState =
  | { readonly status: "bos" }
  | { readonly status: "bekliyor" }
  | { readonly status: "yukleniyor" }
  | {
      readonly status: "hazir";
      readonly candidates: readonly ResolveCandidateDto[];
      readonly catalogSyncedAt: string | null;
      /** Dış arama bu sorguda SORULAMADI mı (hız sınırı / kaynak hatası). */
      readonly disAramaYapilamadi: boolean;
    }
  | { readonly status: "hata"; readonly error: unknown };

export interface InstrumentSearchProps {
  readonly inputId: string;
  readonly onSelect: (candidate: ResolveCandidateDto) => void;
  /** "Bulamadım" yolu — elle değerlenen varlığa geçiş. ŞART. */
  readonly onNotFound: () => void;
  readonly debounceMs?: number;
  readonly disabled?: boolean;
}

export function InstrumentSearch({
  inputId,
  onSelect,
  onNotFound,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  disabled = false,
}: InstrumentSearchProps) {
  const { central, unavailable } = useCentral();
  const listId = useId();
  const optionIdPrefix = useId();

  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>({ status: "bos" });
  /** −1 = hiçbir aday etkin değil. Enter bu hâlde HİÇBİR ŞEY seçmez. */
  const [active, setActive] = useState(-1);
  const seq = useRef(0);

  useEffect(() => {
    if (central === null) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setState({ status: "bos" });
      setActive(-1);
      return;
    }
    setState({ status: "bekliyor" });
    const timer = setTimeout(() => {
      seq.current += 1;
      const mine = seq.current;
      setState({ status: "yukleniyor" });
      central
        .resolve(trimmed, { limit: 10 })
        .then((response) => {
          // ESKİ YANIT YENİYİ EZMEZ.
          if (mine !== seq.current) return;
          setState({
            status: "hazir",
            candidates: response.candidates,
            catalogSyncedAt: response.catalogSyncedAt,
            disAramaYapilamadi:
              response.remoteLookup === "butce-yok" || response.remoteLookup === "hata",
          });
          setActive(-1);
        })
        .catch((error: unknown) => {
          if (mine !== seq.current) return;
          setState({ status: "hata", error });
        });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, central, debounceMs]);

  const candidates = state.status === "hazir" ? state.candidates : [];
  const open = state.status === "hazir" && candidates.length > 0;

  const commit = (index: number): void => {
    const candidate = candidates[index];
    if (candidate === undefined) return;
    onSelect(candidate);
    setQuery("");
    setState({ status: "bos" });
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (candidates.length > 0) setActive((i) => (i + 1) % candidates.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (candidates.length > 0) {
        setActive((i) => (i <= 0 ? candidates.length - 1 : i - 1));
      }
      return;
    }
    if (event.key === "Home" && candidates.length > 0) {
      event.preventDefault();
      setActive(0);
      return;
    }
    if (event.key === "End" && candidates.length > 0) {
      event.preventDefault();
      setActive(candidates.length - 1);
      return;
    }
    if (event.key === "Enter") {
      // Etkin aday yoksa HİÇBİR ŞEY seçilmez — otomatik seçim yasağı.
      event.preventDefault();
      if (active >= 0) commit(active);
      return;
    }
    if (event.key === "Escape") {
      setState({ status: "bos" });
      setActive(-1);
    }
  };

  if (central === null) {
    return (
      <div className="rounded-pf border border-warn-border bg-warn-soft p-2 text-[12px] text-ink">
        <p className="font-semibold text-warn">{M.izleme.arama.devreDisi}</p>
        <p className="mt-0.5">
          {unavailable ?? M.izleme.arama.merkezeUlasilamiyor} {M.izleme.arama.devreDisiMetin}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <TextInput
        id={inputId}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${optionIdPrefix}-${active}` : undefined}
        value={query}
        disabled={disabled}
        placeholder={M.izleme.arama.yerTutucu}
        onChange={(e) => setQuery(e.currentTarget.value)}
        onKeyDown={onKeyDown}
      />

      {/* Ekran okuyucu duyurusu — durum sessiz kalmaz. */}
      <p role="status" aria-live="polite" className="text-[12px] text-muted">
        {state.status === "bos" && query.trim().length > 0
          ? M.izleme.arama.enAz(MIN_QUERY_LENGTH)
          : state.status === "bekliyor" || state.status === "yukleniyor"
            ? M.izleme.arama.araniyor
            : state.status === "hata"
              ? M.izleme.arama.ulasilamadiNokta
              : state.status === "hazir"
                ? state.disAramaYapilamadi
                  ? candidates.length === 0
                    ? M.izleme.arama.sorulamadiBos
                    : M.izleme.arama.sorulamadiAdaylar(candidates.length)
                  : candidates.length === 0
                    ? M.izleme.arama.eslesmeYok
                    : M.izleme.arama.adaylar(candidates.length)
                : ""}
      </p>

      {/* SORULAMADI ≠ BULUNAMADI (27.09.2026). Merkez başlıkta ayırıyordu
          ama cihaz okumuyordu; hız sınırı dolu bir anda kullanıcı "yok" diye
          okuyup aracı elle değerlemeye geçebilirdi. */}
      {state.status === "hazir" && state.disAramaYapilamadi ? (
        <div role="alert" className="rounded-pf border border-warn/40 bg-warn/10 p-2 text-[12px]">
          <p className="font-semibold text-ink">{M.izleme.arama.sorulamadiBaslik}</p>
          <p className="mt-0.5 text-muted">
            {M.izleme.arama.sorulamadiMetin}
          </p>
        </div>
      ) : null}

      {state.status === "hata" && (
        <div role="alert" className="rounded-pf border border-danger-border bg-danger-soft p-2 text-[12px]">
          <p className="font-semibold text-danger">{M.izleme.arama.ulasilamadi}</p>
          <p className="mt-0.5 text-ink">{errorText(state.error)}</p>
          <p className="mt-0.5 text-muted">
            {M.izleme.arama.calismiyor}
          </p>
        </div>
      )}

      {state.status === "hazir" && candidates.length > 1 && (
        <p className="text-[12px] text-warn">
          {M.izleme.arama.birdenFazlaOnce}
          <strong>{M.izleme.arama.birdenFazlaSiz}</strong>
          {M.izleme.arama.birdenFazlaSonra}
        </p>
      )}

      {state.status === "hazir" && (
        <>
          <ul
            id={listId}
            role="listbox"
            aria-label={M.izleme.arama.adaylarEtiketi}
            className={cx(
              "max-h-64 overflow-auto rounded-pf border border-line bg-surface",
              candidates.length === 0 && "hidden",
            )}
          >
            {candidates.map((candidate, index) => (
              <li
                key={candidate.instrumentId}
                id={`${optionIdPrefix}-${index}`}
                role="option"
                aria-selected={index === active}
                className={cx(
                  "cursor-pointer border-b border-line px-2 py-1.5 text-[13px] last:border-0",
                  index === active ? "bg-accent-soft" : "hover:bg-surface-2",
                )}
                onMouseEnter={() => setActive(index)}
                onClick={() => commit(index)}
              >
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span className="pf-num font-semibold text-ink">{candidate.symbol}</span>
                  <span className="text-ink">{candidate.name}</span>
                  <span className="pf-tag">{candidate.exchange}</span>
                </span>
                <span className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted">
                  <span>{assetClassLabel(candidate.assetClass)}</span>
                  <span>{M.izleme.arama.paraBirimi(currencyLabel(candidate.currency))}</span>
                  <span className="pf-num">
                    {M.izleme.arama.eslesmeGuveni(M.bicim.yuzde(String(Math.round(candidate.score * 100))))}
                  </span>
                  <span>
                    {candidate.trackState === null ? M.izleme.arama.izlenmiyor : M.izleme.arama.merkezIzliyor}
                  </span>
                  <span className="pf-num">{candidate.instrumentId}</span>
                </span>
              </li>
            ))}
          </ul>

          {state.catalogSyncedAt !== null && (
            <p className="text-[11px] text-faint">
              {M.izleme.arama.katalogTazeligi(formatIsoTimestamp(state.catalogSyncedAt))}
            </p>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-0.5">
        <Button onClick={onNotFound}>{M.izleme.arama.bulamadim}</Button>
      </div>
    </div>
  );
}
